// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { getCompanions } from 'src/app/api/remote-api/.portal.generated/fn/system-overview/get-companions';
import { UNKEYED_API } from 'src/app/modules/shared/utils/api-call-state.util';
import { pmGetMagnetType } from '../../../api/remote-api/.patternmatch.generated/fn/pattern-match/pm-get-magnet-type';
import { getModalitySystemOverview } from '../../../api/remote-api/.portal.generated/fn/application-overview/get-modality-system-overview';
import { getSystemInfoReport } from '../../../api/remote-api/.portal.generated/fn/export-system-info/get-system-info-report';
import { sendSystemInfoEmail } from '../../../api/remote-api/.portal.generated/fn/export-system-info/send-system-info-email';
import { getSrsDataCollectionStatus } from '../../../api/remote-api/.portal.generated/fn/jobs/get-srs-data-collection-status';
import { triggerSrsDataCollection } from '../../../api/remote-api/.portal.generated/fn/jobs/trigger-srs-data-collection';
import { postJournalEntry } from '../../../api/remote-api/.portal.generated/fn/journal/post-journal-entry';
import { getSrsConnectivity } from '../../../api/remote-api/.portal.generated/fn/srs-connectivity/get-srs-connectivity';
import { getAnnouncement } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-announcement';
import { getConfiguration } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-configuration';
import { getEntireOverivew } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-entire-overivew';
import { getModalities } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-modalities';
import { getSystemDetails } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-system-details';
import { systemSearch } from '../../../api/remote-api/.portal.generated/fn/system-overview/system-search';

function convertTimes(results: Observable<JobStatus>): Observable<JobStatus> {
  return results.pipe(
    map((result) => {
      if (result) {
        // converting to local tmimezone not sure why moment does
        // not handle this on its own..
        if (result.lastCompletion) {
          result.lastCompletion = moment
            .utc(result.lastCompletion)
            .toISOString();
        }
        if (result.lastTrigger) {
          result.lastTrigger = moment.utc(result.lastTrigger).toISOString();
        }
      }
      return result;
    }),
  );
}

import moment from 'moment';
import {
  JobStatus,
  JournalEntryModel,
  ModalityModel,
  SystemDetailsModel,
} from '../../../api/remote-api/.portal.generated/models';
import {
  MapApiService,
  keyBySerialAndMaterialNumber,
} from '../../shared/services/map-api.service';
import { STATIC_API } from '../../shared/utils/api-call-state.util';
import { SearchHistoryService } from '../search-history-service/search-history.service';
import { getAtApplicationsLink } from 'src/app/api/remote-api/.portal.generated/fn/system-overview/get-at-applications-link';
import { getIntegratedContentAvailability } from 'src/app/api/remote-api/.portal.generated/fn/system-overview/get-integrated-content-availability';
import { GetIntegratedContentAvailability$Params } from 'src/app/api/remote-api/.portal.generated/fn/system-overview/get-integrated-content-availability';

@Injectable({ providedIn: 'root' })
export class SystemOverviewService {
  /**
   * get a system from API
   * expects there be exactly ONE system!
   */
  public systemDetails = this.mapApiService.mapEndpoint(getSystemDetails, {
    processResult: (results) => {
      results = this.logJournalEntry(results);
      const firstElement = results.pipe(map((r) => r[0]));
      return this.logSystemInHistory(firstElement);
    },
    customCacheKey: keyBySerialAndMaterialNumber,
  });

  /**
   * search for a system. does not require materialNumber
   * (old system held in systemDetails can still exist)
   */
  public systemSearch = this.mapApiService.mapEndpoint(systemSearch, {
    // SystemMaterialNumber and modalityId are optional when searching
    processParams: ({
      systemSerialNumber,
      modalityIds,
    }: {
      systemSerialNumber: string;
      modalityIds: number[];
    }) => ({
      systemSerialNumberORFunctionalLocation: systemSerialNumber,
      modalityId: modalityIds,
    }),
  });

  /**
   * triggers a system data refresh task
   */
  public triggerSrsDataCollection = this.mapApiService.mapEndpoint(
    triggerSrsDataCollection,
    {
      processResult: convertTimes,
    },
  );

  /**
   * get current announcement
   */
  public announcement = this.mapApiService.mapEndpoint(getAnnouncement);

  /**
   * triggers gets status of systemDataRefreshTask
   */
  public getSrsDataCollectionStatus = this.mapApiService.mapEndpoint(
    getSrsDataCollectionStatus,
    {
      processResult: convertTimes,
    },
  );

  /**
   * get current system component overview from API
   */
  public entireOverview = this.mapApiService.mapEndpoint(getEntireOverivew, {
    customCacheKey: keyBySerialAndMaterialNumber,
  });

  /**
   * get modalities
   */
  public modalities = this.mapApiService.mapEndpoint(getModalities, {
    // modalities never change
    customCacheKey: STATIC_API,
  });

  /**
   * get companions for system
   */
  public companions = this.mapApiService.mapEndpoint(getCompanions, {
    // modalities never change
    customCacheKey: keyBySerialAndMaterialNumber,
  });

  /**
   * helper: there are some (VERY POORLY DESIGNED) apis that, for whatever reason,
   * want the modality name instead of the id. this provided here as an observable
   * for whatever system is currently in this.systemDetails
   */
  public currentModalityName$ = combineLatest([
    this.systemDetails.lastSuccessfulData$,
    this.modalities.lastSuccessfulData$,
  ]).pipe(
    map(([details, modalities]) => {
      return modalities.find(({ id }) => id === details.modalityId)
        ?.displayName;
    }),
  );

  /**
   * Check if integrated content is available for the system
   */
  public integratedContentAvailability = this.mapApiService.mapEndpoint(
  getIntegratedContentAvailability,
    {
      customCacheKey: (params: GetIntegratedContentAvailability$Params) => 
        `${params.systemSerialNumber}_${params.systemMaterialNumber}_${params.modalityName}`,
    }
  );

  /**
   * get srs connectivity for system
   */
  public srsConnectivity = this.mapApiService.mapEndpoint(getSrsConnectivity);

  /**
   * get configration
   */
  public configuration = this.mapApiService.mapEndpoint(getConfiguration, {
    // never changes over lifetime of app, app would not support that anyhow
    customCacheKey: STATIC_API,
  });

  /**
   * get mi applications
   */
  public miApplicationsForModality = this.mapApiService.mapEndpoint(
    getModalitySystemOverview,
    {
      processParams: ({ modalityId }) => ({
        modalityId,
      }),
      processResult: (results) => results.pipe(map((result) => result.generic)),
    },
  );

  public miApplicationsForSystem = this.mapApiService.mapEndpoint(
    getModalitySystemOverview,
    {
      processParams: ({
        modalityId,
        systemMaterialNumber,
        systemSerialNumber,
      }) => {
        return {
          systemMaterialNumber,
          systemSerialNumber,
          modalityId,
        };
      },
      processResult: (results) => results.pipe(map((result) => result.system)),
    },
  );

  /**
   * TODO: should this go in some sort of MR-specific service!?
   * get magnet type of an MR system
   * used to send people to differen parts catalogues or something!?
   */
  public magnetType = this.mapApiService.mapEndpoint(pmGetMagnetType, {
    customCacheKey: keyBySerialAndMaterialNumber, // changes per system
  });

  /**
   * insert a journal entry for usage tracking
   * NOTE: there are discussions going to to remove this and use appInsights instead!
   * only used internally
   */
  private postJournalEntry = this.mapApiService.mapEndpoint(postJournalEntry);

  constructor(
    public searchHistoryService: SearchHistoryService,
    private mapApiService: MapApiService,
  ) {}

  /**
   * clears all data for current system!
   * call this when you load a new system
   */
  clearStaleData(): void {
    this.systemDetails.reset();
    this.systemSearch.reset();
    this.entireOverview.reset();
    this.companions.reset();
  }

  // logs journal entries
  private logJournalEntry(results: Observable<SystemDetailsModel[]>) {
    const addJournalEntry = (
      systemDetails: SystemDetailsModel[],
      modalities: readonly ModalityModel[],
    ) => {
      if (Array.isArray(systemDetails)) {
        systemDetails.forEach((system: SystemDetailsModel) => {
          const journalEntry: JournalEntryModel = {
            systemSerialNumber: system.systemSerialNumber,
            systemMaterialNumber: system.systemMaterialNumber,
            isSystemSupported: true,
            //ticketNumber: params.,
            businessLine: modalities.find((m) => m.id === system.modalityId)!
              .displayName,
            wasSystemSerialNumberFound: true,
          };

          this.postJournalEntry
            .request({
              body: journalEntry,
            })
            .toPromise();
        });
      }
    };
    return results.pipe(
      tap((systemDetails: SystemDetailsModel[]) => {
        // we need to resolve the modalities list first
        // so we can map the modality id do the modality name
        this.modalities.lastSuccessfulData$
          .pipe(first())
          .subscribe((modalities) => {
            if (modalities) {
              addJournalEntry(systemDetails, modalities);
            }
          });
        return systemDetails;
      }),
    );
  }

  /**
   * puts a system in the recent system history
   * should be invoked whenever a user 'opens' a system
   * should NOT be invoked when a user 'searches' for a system (and might not find one)
   */
  private logSystemInHistory(
    result: Observable<SystemDetailsModel>,
  ): Observable<SystemDetailsModel> {
    // log in search history
    return result.pipe(
      map((systemDetails) => {
        this.searchHistoryService.addItem(systemDetails);
        return systemDetails;
      }),
    );
  }

  /**
   * get systemInfoReport
   */
  public systemInfoReport = this.mapApiService.mapEndpoint(
    getSystemInfoReport,
    {
      customCacheKey: UNKEYED_API,
    },
  );

  /**
   * Email systemInfoReport
   */
  public emailSystemInfoReport = this.mapApiService.mapEndpoint(
    sendSystemInfoEmail,
    {
      customCacheKey: UNKEYED_API,
    },
  );

  public atApplicationsLink = this.mapApiService.mapEndpoint(
    getAtApplicationsLink,
  );
}
